// A quick look at one record an agent's reply links to (a source), opened by a press on its chip. It
// reads the record once (fetchAgentRef: the server checks that the viewer may open it, and audits the
// read), then, by its kind (refKinds' opener):
// - a fax or a report: a modal titled with its label, showing the document in a frame, with "Open full
//   document" (a new tab) and Close. Only the kinds of document a browser shows safely are framed (a
//   PDF, a PNG, JPEG, GIF or WebP image, plain text): anything else ("This document can't be shown
//   here.") still opens in a new tab;
// - a visit or a patient: a small modal with the patient, the date, the provider and the visit type (for
//   a patient, the date of the latest visit), with "Open visit" or "Open chart", which goes to that
//   visit in this tab (at /clinic/ for a role with the clinic page, as the app's other links to a chart
//   go, else at /visit/). An answer with nothing to show and no visit to open can't be opened;
// - a task: no modal. Messages opens on the task, and the preview is done.
// When the read fails, or its answer isn't what the kind should have (a document address that isn't a
// web address included), the modal says "That record can't be opened." It is never read again on its
// own; pressing the chip again does.
// Focus moves into the modal and stays inside it, Escape closes it, and focus goes back to the chip
// (useDialogFocus). Like the other dialogs it is drawn in the page itself, with no portal.
//
// Props: agentId; runId (the reply's run); item (the ref that was pressed); onClose: the preview is done.
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAgentRef } from './agentsService';
import { refKind, refLabel } from './refKinds';
import useDialogFocus from './useDialogFocus';
import { getSupabaseRole } from '../../../components/supabaseClient';
import { canAccessPage } from '../../../config/roles';
import { openMessages } from '../../../utils/messagingBus';
import { buildVisitNavigation } from '../../../utils/visitPathUtils';

// The character the words use that isn't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const CANT = "That record can't be opened.";
const NOT_HERE = "This document can't be shown here.";
// Only a web address is framed or opened in a new tab: never a script, data or relative one.
const WEB = /^https?:\/\//i;
// The kinds of document a browser shows safely in a frame. A fax falls back to TIFF and a report can be
// HEIC or TIFF, which a browser downloads rather than shows; an SVG or a page would run its scripts in
// the frame, which has no sandbox (a sandbox would keep a PDF from showing).
const FRAMED = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/plain']);

const SCRIM = 'fixed inset-0 z-[70] bg-black/40 dark:bg-black/60 flex items-center justify-center p-4';
const DIALOG = 'w-full max-h-full overflow-y-auto rounded-xl bg-white dark:bg-[#0f0f16] border border-slate-200 dark:border-slate-800 p-4 space-y-3 shadow-2xl outline-none';
const NARROW = 'max-w-md';
const WIDE = 'max-w-3xl';
const TITLE = 'text-sm font-bold text-slate-900 dark:text-slate-100 break-words';
const QUIET = 'text-xs text-slate-500 dark:text-slate-400';
const ERROR = 'text-xs text-red-600 dark:text-red-400';
const FRAME = 'block w-full h-[70vh] rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900';
const FACTS = 'grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 m-0 text-xs';
const TERM = 'text-slate-500 dark:text-slate-400';
const VALUE = 'm-0 min-w-0 break-words text-slate-800 dark:text-slate-100';
const ACTIONS = 'flex flex-wrap items-center justify-end gap-2';
const PRIMARY = 'rounded-md px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white';
const SECONDARY = 'rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const textOf = (value) => (typeof value === 'string' && value.trim() ? value : null);
const upperFirst = (words) => words.charAt(0).toUpperCase() + words.slice(1);
// A content type as FRAMED has them: its media type, in lower case, without parameters.
const mediaType = (value) => (typeof value === 'string' ? value.split(';')[0].trim().toLowerCase() : '');

// What the preview shows of the server's answer for a ref of kind `name` (`kind`, its registry entry),
// or null when the answer isn't what that kind should have, or has nothing to show. Its label is null
// when the answer has none.
function readAnswer(name, kind, answer) {
  if (!isObject(answer) || (answer.kind !== undefined && answer.kind !== name)) return null;
  const label = textOf(answer.label);
  if (kind.opener === 'document') {
    if (typeof answer.url !== 'string' || !WEB.test(answer.url)) return null;
    return { label, url: answer.url, contentType: mediaType(answer.content_type) };
  }
  if (kind.opener === 'chart') {
    const open = isObject(answer.open) && textOf(answer.open.uuid) ? answer.open : null;
    const facts = (name === 'patient'
      ? [['Patient', answer.patient], ['Latest visit', open?.date]]
      : [['Patient', answer.patient], ['Date', answer.date], ['Provider', answer.provider], ['Visit type', answer.visit_type]])
      .filter(([, value]) => textOf(value));
    return open || facts.length ? { label, open, facts } : null;
  }
  const taskId = typeof answer.task_id === 'number' && Number.isFinite(answer.task_id)
    ? String(answer.task_id) : textOf(answer.task_id);
  return taskId ? { label, taskId } : null;
}

// The modal, as the agents dialogs draw theirs, with Close last among its buttons.
function PreviewDialog({ title, wide, actions = null, onClose, children }) {
  const dialog = useRef(null);
  const titleId = useId();
  useDialogFocus(dialog, onClose);
  return (
    <div className={SCRIM}>
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
           className={`${DIALOG} ${wide ? WIDE : NARROW}`}>
        <div id={titleId} className={TITLE}>{title}</div>
        {children}
        <div className={ACTIONS}>
          {actions}
          <button type="button" onClick={onClose} className={SECONDARY}>Close</button>
        </div>
      </div>
    </div>
  );
}

// "Open visit" or "Open chart": the visit's page in this tab, with its schema and date in the address
// (and in the router's state, as the clinic page also reads them there). The page is /clinic/ for a role
// with the clinic page, else /visit/, as everywhere else the app opens a chart; the role is the one read
// at sign-in. No row is handed over: the page reads the visit itself.
function OpenChart({ open, words, onClose }) {
  const navigate = useNavigate();
  const go = () => {
    const prefix = canAccessPage(getSupabaseRole(), 'clinicPage') ? 'clinic' : 'visit';
    const nav = buildVisitNavigation(
      { id: encodeURIComponent(open.uuid), date_: textOf(open.date) || '', _schema: textOf(open.schema) || '' },
      prefix,
    );
    onClose?.();
    navigate(nav.to, { state: { date_: nav.state.date_, _schema: nav.state._schema } });
  };
  return <button type="button" onClick={go} className={PRIMARY}>{words}</button>;
}

export default function SourcePreview({ agentId, runId = null, item, onClose }) {
  const name = item?.kind;
  const kind = refKind(name);
  const key = item?.key;
  const readable = !!kind && item.available === true && typeof key === 'string' && !!key && !!runId;
  const [state, setState] = useState(() => (readable ? { step: 'loading' } : { step: 'failed' }));
  const done = useRef(onClose);
  done.current = onClose;

  useEffect(() => {
    if (!readable) return undefined;
    let live = true;
    let request;
    try {
      request = Promise.resolve(fetchAgentRef(agentId, runId, key));
    } catch (err) {
      request = Promise.reject(err);
    }
    request
      .then((answer) => {
        if (!live) return;
        const read = readAnswer(name, kind, answer);
        if (!read) {
          setState({ step: 'failed' });
        } else if (kind.opener === 'messages') {
          openMessages({ tab: 'tasks', focus: { kind: 'task', id: read.taskId } });
          done.current?.();
        } else {
          setState({ step: 'ready', answer: read });
        }
      }, () => {
        if (live) setState({ step: 'failed' });
      });
    return () => { live = false; };
  }, [agentId, runId, key, name, kind, readable]);

  // A task has no modal while it is read, nor once Messages has opened on it.
  if (kind?.opener === 'messages' && state.step !== 'failed') return null;

  const { answer } = state;
  const label = answer?.label || refLabel(item) || 'a record';
  const framed = !!answer && kind.opener === 'document' && FRAMED.has(answer.contentType);
  const close = () => done.current?.();
  let body = <div role="status" className={QUIET}>{`Loading${ELLIPSIS}`}</div>;
  let actions = null;
  if (state.step === 'failed') {
    body = <div role="alert" className={ERROR}>{CANT}</div>;
  } else if (answer && kind.opener === 'document') {
    // Out of the tab order: from inside the frame (another page) the dialog hears neither Tab nor Escape.
    body = framed
      ? <iframe title={label} src={answer.url} referrerPolicy="no-referrer" tabIndex={-1} className={FRAME} />
      : <div className={QUIET}>{NOT_HERE}</div>;
    actions = (
      <button type="button" onClick={() => window.open(answer.url, '_blank', 'noopener,noreferrer')} className={PRIMARY}>
        Open full document
      </button>
    );
  } else if (answer) {
    body = answer.facts.length > 0 && (
      <dl className={FACTS}>
        {answer.facts.map(([term, value]) => (
          <React.Fragment key={term}>
            <dt className={TERM}>{term}</dt>
            <dd className={VALUE}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    );
    actions = answer.open && <OpenChart open={answer.open} words={kind.button} onClose={close} />;
  }
  const wide = kind?.opener === 'document' && (state.step === 'loading' || framed);
  return (
    <PreviewDialog title={upperFirst(label)} wide={wide} actions={actions} onClose={close}>
      {body}
    </PreviewDialog>
  );
}
